import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { To } from "react-router";
import { useNavigate } from "react-router";

// Replacement for `history.block()` as the mechanism `useNavigationBlocker`
// (see ./use-navigation-blocker.ts) guards navigation with.
//
// `history.block()` used to transparently intercept *any* navigation (any
// `<Link>` click, any `history.push()` call, real browser back/forward)
// because it hooked the one shared `history` object that drove the whole
// router (`<HistoryRouter>`, retired in Phase 4 - the app now runs a plain
// `<BrowserRouter>`). React Router's own equivalent (`useBlocker`) needs
// the data router (`createBrowserRouter`/`RouterProvider`), which is a
// bigger paradigm change kept explicitly out of scope.
//
// This registry is the narrower, deliberate replacement. It doesn't try to
// intercept navigation the same way `history.block()` did (there's no way
// to, without the data router) - instead it exposes `attemptNavigate()`,
// which every navigation *entry point* that should respect an active
// guard calls before actually moving. Today that's exactly one entry
// point: `<Button to="...">` (`src/common/button/index.tsx`), which covers
// the overwhelming majority of in-app navigation UI - it renders
// react-router's own `<Link>` and composes with `<Link>`'s built-in
// onClick-composition pattern (call the consumer's onClick, check
// `event.defaultPrevented`, only then navigate), so blocking here is a
// standard, supported use of react-router's public API, not a hack.
//
// Known gaps, accepted rather than solved here:
//   - The 5 raw `<Link>` usages elsewhere in the app aren't wired to this
//     registry - same fix as Button's, just not done.
//   - Imperative `navigate()` calls elsewhere in the app (the former
//     `history.push()`/`.replace()` call sites, now converted to
//     `useNavigate()`) don't check this registry - most are effect-driven
//     redirects, not user-initiated escapes, so this is a smaller gap than
//     it sounds, but it isn't zero.
//   - Real browser back/forward button presses aren't guarded. `history`
//     v5's `.block()` could do this because it hooked `popstate` *before*
//     the browser's session history index (and thus `window.location`)
//     actually moved. A plain `popstate` listener on top of `BrowserRouter`
//     only sees the change *after* it's already happened - there's no way
//     to cleanly "undo" it without a visible flicker, so this needs the
//     data router same as the point above.
//   - Tab close/reload gets the browser's native confirmation instead of
//     the app's popup - see the `beforeunload` listener below.
//
// Simplification: `pendingTransition` is shared context state, not
// per-guard. Fine today (exactly one real consumer,
// `useNavigationBlocker`, ever registers a guard at a time) - if a second
// concurrent consumer shows up, this needs to become per-guard state
// instead of a single shared slot.

type Transition = { action: "PUSH" | "REPLACE"; path: To; state?: unknown };

interface NavigationGuardContextValue {
  registerGuard: (isActive: () => boolean) => () => void;
  attemptNavigate: (
    action: "PUSH" | "REPLACE",
    path: To,
    state?: unknown
  ) => boolean;
  pendingTransition: Transition | null;
  confirmPendingTransition: () => void;
  cancelPendingTransition: () => void;
}

const NavigationGuardContext =
  createContext<NavigationGuardContextValue | null>(null);

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const guardsRef = useRef<Set<() => boolean>>(new Set());
  const [pendingTransition, setPendingTransition] = useState<Transition | null>(
    null
  );

  const registerGuard = useCallback((isActive: () => boolean) => {
    guardsRef.current.add(isActive);
    return () => {
      guardsRef.current.delete(isActive);
    };
  }, []);

  const isBlocked = useCallback(
    () =>
      Array.from(guardsRef.current).some((guardIsActive) => guardIsActive()),
    []
  );

  const performTransition = useCallback(
    ({ action, path, state }: Transition) => {
      navigate(path, { replace: action === "REPLACE", state });
    },
    [navigate]
  );

  const attemptNavigate = useCallback(
    (action: "PUSH" | "REPLACE", path: To, state?: unknown) => {
      if (isBlocked()) {
        setPendingTransition({ action, path, state });
        return false;
      }
      performTransition({ action, path, state });
      return true;
    },
    [isBlocked, performTransition]
  );

  const confirmPendingTransition = useCallback(() => {
    if (pendingTransition) {
      performTransition(pendingTransition);
    }
    setPendingTransition(null);
  }, [pendingTransition, performTransition]);

  const cancelPendingTransition = useCallback(() => {
    setPendingTransition(null);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isBlocked()) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isBlocked]);

  const value = useMemo<NavigationGuardContextValue>(
    () => ({
      registerGuard,
      attemptNavigate,
      pendingTransition,
      confirmPendingTransition,
      cancelPendingTransition,
    }),
    [
      registerGuard,
      attemptNavigate,
      pendingTransition,
      confirmPendingTransition,
      cancelPendingTransition,
    ]
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) {
    throw new Error(
      "useNavigationGuard must be used within a NavigationGuardProvider"
    );
  }
  return ctx;
}
